import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import TaskBadge from "@/components/TaskBadge";
import {
  Play,
  Pause,
  MicVocal,
  Search,
  Download,
  VolumeX,
  Volume2,
  X,
  HardDriveDownload,
  Loader2,
} from "lucide-react";

function fmt(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = String(Math.floor(sec % 60)).padStart(2, "0");
  return `${m}:${s}`;
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Recordings() {
  const [calls, setCalls] = useState([]);
  const [q, setQ] = useState("");
  const [current, setCurrent] = useState(null); // full call object
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  // call id -> true while the recorder PC is uploading an older recording
  const [fetching, setFetching] = useState({});
  const [searchParams] = useSearchParams();
  const wantedCall = searchParams.get("call");
  const handledWanted = useRef(false);

  const loadCalls = async () => {
    const { data } = await api.get("/calls");
    setCalls(data);
    return data;
  };

  useEffect(() => {
    loadCalls();
  }, []);

  const startPlaying = (call) => {
    setCurrent(call);
    setLoading(true);
    setProgress(0);
    setDuration(call.duration_sec || 0);
  };

  // Recordings older than the newest 30 live on the recorder PC. Ask it to
  // upload this one again, wait for it, then play it.
  const fetchFromPc = async (call) => {
    if (fetching[call.id]) return;
    setFetching((f) => ({ ...f, [call.id]: true }));
    const done = () => setFetching((f) => ({ ...f, [call.id]: false }));
    try {
      const { data } = await api.post(`/calls/${call.id}/recording/fetch`);
      if (data.status !== "ready") {
        toast.message("Asking the recorder PC for this recording…");
        const deadline = Date.now() + 3 * 60 * 1000;
        for (;;) {
          await new Promise((r) => setTimeout(r, 3000));
          const { data: st } = await api.get(`/calls/${call.id}/recording/status`);
          if (st.status === "ready") break;
          if (st.status === "failed") {
            toast.error(st.detail || "The recorder PC couldn't send this recording.");
            done();
            return;
          }
          if (Date.now() > deadline) {
            toast.error("The recorder PC hasn't sent it yet. Check that it's switched on, then try again.");
            done();
            return;
          }
        }
      }
      const fresh = await loadCalls();
      const updated = fresh.find((c) => c.id === call.id);
      done();
      if (updated && updated.recording_url) {
        toast.success("Recording ready. It stays playable here for 24 hours.");
        startPlaying(updated);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't request the recording.");
      done();
    }
  };

  // Opened from Call Logs with ?call=<id>: play it, or fetch it from the PC.
  useEffect(() => {
    if (!wantedCall || handledWanted.current || calls.length === 0) return;
    const c = calls.find((x) => x.id === wantedCall);
    if (!c) return;
    handledWanted.current = true;
    if (c.recording_url) startPlaying(c);
    else if (c.recording_on_pc) fetchFromPc(c);
  }, [calls, wantedCall]);

  const filtered = calls.filter((c) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (
      (c.customer_name || "").toLowerCase().includes(t) ||
      (c.customer_phone || "").includes(t) ||
      (c.ivr_path || "").includes(t) ||
      (c.task_no != null && `#${c.task_no}`.includes(t))
    );
  });

  // Play / pause / switch
  const playCall = async (call) => {
    if (!call.recording_url) {
      toast.error("No recording available for this call.");
      return;
    }
    // Same call → toggle
    if (current && current.id === call.id && audioRef.current) {
      if (playing) {
        audioRef.current.pause();
      } else {
        try {
          await audioRef.current.play();
        } catch (e) {
          toast.error("Browser blocked audio playback. Try again.");
        }
      }
      return;
    }
    setCurrent(call);
    setLoading(true);
    setProgress(0);
    setDuration(call.duration_sec || 0);
  };

  // When `current` changes, set new src and start
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !current) return;
    el.src = `${API}/calls/${current.id}/recording`;
    el.load();
    el.play().catch(() => {
      toast.error("Could not start playback. Check that the recording exists.");
      setLoading(false);
    });
  }, [current]);

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
    setCurrent(null);
    setProgress(0);
  };

  const seek = (pct) => {
    const el = audioRef.current;
    if (!el || !isFinite(el.duration)) return;
    el.currentTime = (pct / 100) * el.duration;
    setProgress(pct);
  };

  const onLoaded = () => {
    setLoading(false);
    if (audioRef.current) setDuration(audioRef.current.duration || duration);
  };

  const onTime = () => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    setProgress((el.currentTime / el.duration) * 100);
  };

  const download = (call) => {
    if (!call.recording_url) return;
    const link = document.createElement("a");
    link.href = `${API}/calls/${call.id}/recording`;
    link.download = `${call.customer_name || "call"}-${call.id.slice(0, 8)}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="p-6 md:p-10 max-w-[1400px] pb-32"
      data-testid="owner-recordings"
    >
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
            Voice
          </div>
          <h1 className="font-heading text-3xl font-bold mt-2">
            Call recordings
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            The newest 30 recordings are kept on the server and play
            instantly. Older ones stay on the recorder PC for 3 months: use
            &ldquo;Get from PC&rdquo; to bring one back.
          </p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search name, phone or IVR path"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 w-[280px]"
            data-testid="recordings-search"
          />
        </div>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <MicVocal className="w-5 h-5 text-[#0055FF]" />
            {filtered.length} recordings
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {filtered.map((c) => {
              const isCurrent = current?.id === c.id;
              const isPlaying = isCurrent && playing;
              return (
                <div
                  key={c.id}
                  className={`flex md:grid md:grid-cols-12 items-center gap-3 px-4 md:px-5 py-4 ${
                    isCurrent ? "bg-blue-50/60" : "hover:bg-slate-50"
                  }`}
                  data-testid={`recording-${c.id}`}
                >
                  {!c.recording_url && c.recording_on_pc ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => fetchFromPc(c)}
                    disabled={!!fetching[c.id]}
                    title="Get this recording from the recorder PC"
                    className="col-span-1 shrink-0 w-10 h-10 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100"
                    data-testid={`recording-fetch-${c.id}`}
                  >
                    {fetching[c.id] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <HardDriveDownload className="w-4 h-4" />
                    )}
                  </Button>
                  ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => playCall(c)}
                    disabled={!c.recording_url}
                    className={`col-span-1 shrink-0 w-10 h-10 rounded-full ${
                      isPlaying
                        ? "bg-[#FF5000] text-white hover:bg-[#CC4000] hover:text-white"
                        : isCurrent
                          ? "bg-[#0055FF] text-white hover:bg-[#0040CC] hover:text-white"
                          : "bg-slate-100 hover:bg-slate-200"
                    }`}
                    data-testid={`recording-play-${c.id}`}
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4 ml-0.5" />
                    )}
                  </Button>
                  )}
                  <div className="col-span-3 flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {c.customer_name}
                      {!c.recording_url && c.recording_on_pc && (
                        <span className="ml-2 mono text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 align-middle">
                          {fetching[c.id] ? "fetching…" : "on PC"}
                        </span>
                      )}
                    </div>
                    <div className="mono text-[11px] text-slate-500 truncate">
                      <TaskBadge call={c} /> {c.customer_phone}
                    </div>
                    <div
                      className="mono text-[11px] text-slate-500 truncate"
                      data-testid={`recording-date-${c.id}`}
                    >
                      {fmtDate(c.started_at)}
                      {c.ivr_path && <span className="md:hidden"> · IVR {c.ivr_path}</span>}
                    </div>
                  </div>
                  <div className="col-span-3 hidden md:block">
                    {isCurrent ? (
                      <div className="h-6 flex items-center gap-2">
                        <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#0055FF] transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="mono text-[10px] text-slate-500 tabular-nums">
                          {fmt((progress / 100) * (duration || c.duration_sec))}
                        </span>
                      </div>
                    ) : (
                      <div className="h-6 flex items-end gap-[2px]">
                        {Array.from({ length: 30 }).map((_, i) => {
                          const h = 4 + ((i * 7) % 20);
                          return (
                            <span
                              key={i}
                              className="w-[3px] rounded-sm bg-slate-300"
                              style={{ height: `${h}px` }}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 hidden md:block mono text-xs text-slate-500 tabular-nums">
                    IVR {c.ivr_path || "—"}
                  </div>
                  <div className="col-span-2 shrink-0 mono text-xs text-slate-500 tabular-nums">
                    {fmt(c.duration_sec)}
                  </div>
                  <div className="col-span-1 shrink-0 flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => download(c)}
                      disabled={!c.recording_url}
                      className="text-slate-400 hover:text-slate-700"
                      data-testid={`recording-download-${c.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="p-10 text-center text-sm text-slate-500">
                No recordings yet.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sticky mini-player */}
      {current && (
        <div
          className="fixed left-0 right-0 bottom-0 z-40 bg-white/95 backdrop-blur-xl border-t shadow-2xl"
          data-testid="recording-player"
        >
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
            <Button
              size="icon"
              onClick={() => playCall(current)}
              className={`rounded-full w-11 h-11 ${
                playing
                  ? "bg-[#FF5000] hover:bg-[#CC4000]"
                  : "bg-[#0055FF] hover:bg-[#0040CC]"
              }`}
              data-testid="player-play-toggle"
            >
              {loading ? (
                <span className="mono text-[10px]">…</span>
              ) : playing ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4 ml-0.5" />
              )}
            </Button>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <div className="font-medium truncate">
                  {current.customer_name}
                </div>
                <span className="mono text-[10px] text-slate-500 truncate">
                  {current.customer_phone} · {fmtDate(current.started_at)}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="mono text-[10px] text-slate-500 tabular-nums w-9">
                  {fmt((progress / 100) * (duration || 0))}
                </span>
                <Slider
                  value={[progress]}
                  min={0}
                  max={100}
                  step={0.1}
                  onValueChange={(v) => seek(v[0])}
                  className="flex-1"
                  data-testid="player-seek"
                />
                <span className="mono text-[10px] text-slate-500 tabular-nums w-9">
                  {fmt(duration || 0)}
                </span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 w-32">
              <button
                type="button"
                onClick={() => setMuted((m) => !m)}
                className="text-slate-500 hover:text-slate-800"
                data-testid="player-mute"
              >
                {muted || volume === 0 ? (
                  <VolumeX className="w-4 h-4" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </button>
              <Slider
                value={[muted ? 0 : Math.round(volume * 100)]}
                min={0}
                max={100}
                step={1}
                onValueChange={(v) => {
                  const nv = v[0] / 100;
                  setVolume(nv);
                  setMuted(nv === 0);
                }}
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => download(current)}
              className="text-slate-500"
              data-testid="player-download"
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={stop}
              className="text-slate-500"
              data-testid="player-close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Single audio element, controlled programmatically */}
      <audio
        ref={audioRef}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
        }}
        onLoadedMetadata={onLoaded}
        onTimeUpdate={onTime}
        onError={() => {
          setLoading(false);
          setPlaying(false);
          toast.error("Failed to load audio. The recording URL may be invalid.");
        }}
        onVolumeChange={() => {
          if (!audioRef.current) return;
          if (audioRef.current.volume !== volume) {
            audioRef.current.volume = volume;
          }
        }}
        muted={muted}
        style={{ display: "none" }}
      />
      {audioRef.current && (audioRef.current.volume = volume) && null}
    </div>
  );
}
